/* ==========================================================================
   TUCANO · PDP behaviors
   - Gallery thumbnail switching
   - Variant option selection → updates variant id + price + featured image
   - ATC AJAX submit → opens Savor cart drawer
   - Sticky bar visibility on scroll past hero
   ========================================================================== */

(() => {
  'use strict';

  /* -------- Cart drawer: aggiorna il contenuto PRIMA di mostrarlo -------- */
  async function tcnSyncDrawer(sourceId) {
    let cart = null, html = null;
    try {
      const [cr, sr] = await Promise.all([
        fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' }),
        fetch(window.location.pathname + '?sections=tcn-cart-drawer&t=' + Date.now(), { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      ]);
      if (cr.ok) cart = await cr.json();
      if (sr.ok) { const j = await sr.json(); html = j && j['tcn-cart-drawer']; }
    } catch (_) {}
    if (cart) {
      // evita l'apertura automatica immediata del drawer (mostrerebbe il contenuto vecchio/vuoto):
      // lo apriamo noi quando il contenuto è pronto
      const drawerEl = document.querySelector('cart-drawer-component');
      const hadAutoOpen = drawerEl && drawerEl.hasAttribute('auto-open');
      if (hadAutoOpen) drawerEl.removeAttribute('auto-open');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: sourceId || 'tcn',
          data: { source: sourceId || 'tcn', itemCount: cart.item_count, sections: html ? { 'tcn-cart-drawer': html } : {} },
        },
      }));
      if (hadAutoOpen) setTimeout(() => drawerEl.setAttribute('auto-open', ''), 1500);
    }
    return cart;
  }
  async function tcnOpenDrawer() {
    const d = document.querySelector('cart-drawer-component');
    if (!d) return;
    // attende che il contenuto sia stato aggiornato (max ~1,2 s) per non mostrare "carrello vuoto"
    for (let i = 0; i < 30; i++) {
      const empty = d.querySelector('#empty-state, .cart-drawer__items--empty') || d.querySelector('dialog.cart-drawer--empty');
      if (!empty) break;
      await new Promise((r) => setTimeout(r, 40));
    }
    const dialog = d.querySelector('dialog[ref="dialog"], dialog');
    if (dialog && dialog.open) return;
    if (typeof d.open === 'function') { try { d.open(); return; } catch (_) {} }
    if (dialog && typeof dialog.showModal === 'function') { try { dialog.showModal(); } catch (_) {} }
  }

  const root = document.querySelector('[data-tcn-pdp]');
  if (!root) return;

  const productHandle = root.dataset.productHandle;
  let productData = null;

  /* -------- Load product JSON for variant matching -------- */
  async function loadProduct() {
    try {
      const res = await fetch(`/products/${productHandle}.js`, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Product fetch failed');
      productData = await res.json();
    } catch (e) {
      console.warn('[Tucano PDP] Could not load product JSON', e);
    }
  }

  /* -------- Gallery: thumb click switches slide + variant-scoped filter -------- */
  let activeIdx = 0;
  let visibleIndices = [];

  function setupGallery() {
    const thumbs = root.querySelectorAll('[data-tcn-thumb]');
    const slides = root.querySelectorAll('[data-tcn-slide]');
    const counter = root.querySelector('[data-tcn-counter-cur]');
    if (!thumbs.length) return;

    // Initialize visibleIndices to all
    visibleIndices = Array.from(slides).map((_, i) => i);

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => activate(i));
    });

    // Swipe support on mobile (basic) — only cycle through visible
    const stage = root.querySelector('[data-tcn-stage]');
    if (stage) {
      let startX = 0;
      stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < 50) return;
        const curPos = visibleIndices.indexOf(activeIdx);
        if (curPos === -1) return;
        const nextPos = dx < 0
          ? Math.min(curPos + 1, visibleIndices.length - 1)
          : Math.max(curPos - 1, 0);
        activate(visibleIndices[nextPos]);
      }, { passive: true });
    }
  }

  function activate(idx) {
    const thumbs = root.querySelectorAll('[data-tcn-thumb]');
    const slides = root.querySelectorAll('[data-tcn-slide]');
    const counter = root.querySelector('[data-tcn-counter-cur]');
    activeIdx = idx;
    thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
    slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    if (counter) {
      const visiblePos = visibleIndices.indexOf(idx) + 1;
      counter.textContent = String(visiblePos > 0 ? visiblePos : idx + 1).padStart(2, '0');
    }
  }

  /* -------- Filter media by variant range --------
     Robust handling for products where MULTIPLE variants share the same
     featured_media (e.g. 5 compatibility × 7 colors = 35 variants but only
     7 distinct media groups, one per color). We dedupe positions, sort,
     then take [currentPos … nextDistinctPos - 1] as the range. */
  function applyVariantMediaScope(variant) {
    if (!productData || !variant) return;

    const slides = root.querySelectorAll('[data-tcn-slide]');
    const thumbs = root.querySelectorAll('[data-tcn-thumb]');

    const showAll = () => {
      visibleIndices = Array.from(slides).map((_, i) => i);
      slides.forEach(s => { s.style.display = ''; });
      thumbs.forEach(t => { t.style.display = ''; });
      currentVariantRange = null;
      refresh3DBadge();
      // ODDS stack: mostra tutto tranne il primo (è nella hero)
      const stackItems = document.querySelectorAll('[data-tcn-odds-stack-item]');
      stackItems.forEach((it, idx) => { it.hidden = idx === 0; });
    };

    if (!variant.featured_media) {
      showAll();
      return;
    }

    // Build sorted list of UNIQUE positions across all variants
    const positions = Array.from(new Set(
      (productData.variants || [])
        .filter(v => v.featured_media && v.featured_media.position)
        .map(v => v.featured_media.position)
    )).sort((a, b) => a - b);

    if (positions.length === 0) {
      showAll();
      return;
    }

    const myPos = variant.featured_media.position;
    const idx = positions.indexOf(myPos);
    if (idx === -1) {
      showAll();
      return;
    }

    const startPos = positions[idx];
    const endPos = idx + 1 < positions.length ? positions[idx + 1] - 1 : Infinity;
    currentVariantRange = { start: startPos, end: endPos };

    // Apply visibility — use data-position attribute (more reliable than index)
    visibleIndices = [];
    slides.forEach((slide, i) => {
      const pos = parseInt(slide.dataset.position, 10) || (i + 1);
      const inRange = pos >= startPos && pos <= endPos;
      slide.style.display = inRange ? '' : 'none';
      const matchingThumb = thumbs[i];
      if (matchingThumb) matchingThumb.style.display = inRange ? '' : 'none';
      if (inRange) visibleIndices.push(i);
    });

    // Activate the variant's specific featured_media position
    const targetIdx = visibleIndices.find(i => {
      const pos = parseInt(slides[i].dataset.position, 10);
      return pos === myPos;
    });
    if (targetIdx != null) {
      activate(targetIdx);
    } else if (visibleIndices.length > 0) {
      activate(visibleIndices[0]);
    }

    // P1.6 — il set di media visibili è cambiato: ricontrolla il badge 3D
    refresh3DBadge();

    // ODDS — filtra il media stack sotto la hero per la variante corrente.
    // Mostra solo i media in [startPos+1, endPos] (saltando l'hero = startPos)
    const stackItems = document.querySelectorAll('[data-tcn-odds-stack-item]');
    if (stackItems.length) {
      stackItems.forEach((it) => {
        const pos = parseInt(it.dataset.position, 10);
        const inStack = pos > startPos && pos <= endPos;
        it.hidden = !inStack;
      });
    }
  }

  /* -------- Variant selection -------- */
  function setupVariants() {
    const opts = root.querySelectorAll('[data-tcn-opt]');
    if (!opts.length) return;
    const variantInput = root.querySelector('[data-tcn-variant-input]');
    const atc = root.querySelector('[data-tcn-atc]');
    const atcLabel = root.querySelector('[data-tcn-atc-label]');
    const priceBlock = root.querySelector('[data-tcn-price]');
    const skuLabel = root.querySelector('[data-tcn-sku]');
    const stockEl = root.querySelector('[data-tcn-stock]');

    const getSelectedOptions = () => {
      const selected = [];
      opts.forEach((opt) => {
        const checked = opt.querySelector('[data-tcn-opt-radio]:checked');
        if (checked) selected.push(checked.value);
        const valueLabel = opt.querySelector('[data-tcn-opt-value]');
        if (valueLabel && checked) valueLabel.textContent = checked.value;
      });
      return selected;
    };

    const findVariant = (selected) => {
      if (!productData) return null;
      return productData.variants.find((v) => {
        return v.options.every((opt, idx) => opt === selected[idx]);
      });
    };

    const formatMoney = (cents) => {
      // Stesso formato del resto del sito (es. "€78,00")
      const fmt = window.Shopify?.currency?.active || 'EUR';
      const symbol = fmt === 'EUR' ? '€' : fmt + ' ';
      try {
        return symbol + new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
      } catch {
        return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
      }
    };

    const updateVariant = () => {
      const selected = getSelectedOptions();
      const variant = findVariant(selected);
      if (!variant) return;

      variantInput.value = variant.id;

      // Notify other components (cabin-check, fit-checker, etc.) of the variant change.
      // Native `change` event so descendants can listen on the input, plus a
      // bubbling custom event for global listeners.
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('tcn:variant-change', {
        bubbles: true,
        detail: { variant, variantId: variant.id, selectedOptions: selected },
      }));

      // Update hero "Disegnato per" chip — only for custodia-tablet category.
      // Other categories keep product-level disegnato_per (static).
      const pdpCat = (root.dataset.tcnCategoryPdp || '').toLowerCase();
      if (pdpCat === 'custodia-tablet') {
        const designedChip = root.querySelector('[data-tcn-hero-designed]');
        const designedVal = root.querySelector('[data-tcn-hero-designed-val]');
        if (designedChip && designedVal) {
          const map = window.__tcnVariantRetro || {};
          const retro = map[variant.id];
          if (retro) {
            designedVal.textContent = retro;
            designedChip.hidden = false;
          } else {
            designedChip.hidden = true;
            designedVal.textContent = '';
          }
        }
      }

      // Filter media to only show this variant's range
      applyVariantMediaScope(variant);

      // Update price block (separate from ATC)
      if (priceBlock) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          priceBlock.innerHTML =
            `<span class="tcn-pdp-hero__price-sale">${formatMoney(variant.price)}</span>` +
            `<s class="tcn-pdp-hero__price-compare">${formatMoney(variant.compare_at_price)}</s>` +
            `<span class="tcn-badge tcn-badge--sale">-${pct}%</span>`;
        } else {
          priceBlock.innerHTML = `<span class="tcn-pdp-hero__price-current">${formatMoney(variant.price)}</span>`;
        }
      }

      // Prezzo più basso negli ultimi 30 giorni (Omnibus)
      const omWrap = root.querySelector('[data-tcn-omnibus-wrap]');
      const omMapEl = root.querySelector('[data-tcn-omnibus-map]');
      if (omWrap && omMapEl) {
        let omMap = {};
        try { omMap = JSON.parse(omMapEl.textContent); } catch (_) {}
        const omText = omMap[String(variant.id)] || '';
        omWrap.innerHTML = omText ? '<p class="tcn-omnibus" data-tcn-omnibus></p>' : '';
        if (omText) omWrap.firstChild.textContent = omText;
      }

      // Update SKU
      // Variante senza SKU: nascondi l'etichetta invece di lasciare quello precedente
      if (skuLabel) {
        skuLabel.textContent = variant.sku ? 'SKU ' + variant.sku : '';
        skuLabel.hidden = !variant.sku;
      }

      // Update stock indicator
      if (stockEl) {
        if (variant.available) {
          if (variant.inventory_management && typeof variant.inventory_quantity === 'number' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
            stockEl.innerHTML = `<span class="tcn-pdp-hero__stock-dot tcn-pdp-hero__stock-dot--low"></span>${(stockEl.dataset.onlyLeft || 'Solo __N__ rimasti').replace('__N__', variant.inventory_quantity)}`;
          } else {
            stockEl.innerHTML = '';
          }
        } else {
          stockEl.innerHTML = `<span class="tcn-pdp-hero__stock-dot tcn-pdp-hero__stock-dot--ko"></span>${stockEl.dataset.soldOut || 'Esaurito'}`;
        }
      }

      // Update ATC state
      if (atc) {
        if (variant.available) {
          atc.removeAttribute('disabled');
          if (atcLabel) atcLabel.textContent = (window.tcnI18n || {}).addToCart || 'Aggiungi al carrello';
        } else {
          atc.setAttribute('disabled', 'disabled');
          if (atcLabel) atcLabel.textContent = (window.tcnI18n || {}).soldOut || 'Esaurito';
        }
      }

      // Update URL with ?variant=
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update sticky bar price too
      const stickyPrice = document.querySelector('.tcn-pdp-stickybar__price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    };

    opts.forEach((opt) => {
      opt.querySelectorAll('[data-tcn-opt-radio]').forEach((r) => {
        r.addEventListener('change', updateVariant);
      });
    });

    // Apply variant scope on initial load (for pre-selected variant)
    const initialSelected = getSelectedOptions();
    const initialVariant = findVariant(initialSelected);
    if (initialVariant) applyVariantMediaScope(initialVariant);
  }

  /* -------- Quantity stepper -------- */
  function setupQty() {
    const minus = root.querySelector('[data-tcn-qty-minus]');
    const plus = root.querySelector('[data-tcn-qty-plus]');
    const input = root.querySelector('[data-tcn-qty-input]');
    if (!minus || !plus || !input) return;

    const update = () => {
      const v = Math.max(1, parseInt(input.value, 10) || 1);
      input.value = v;
      minus.disabled = v <= 1;
    };

    minus.addEventListener('click', () => {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      update();
    });
    plus.addEventListener('click', () => {
      input.value = (parseInt(input.value, 10) || 1) + 1;
      update();
    });
    input.addEventListener('input', update);
    update();
  }

  /* -------- ATC AJAX submit (minimale, bulletproof) -------- */
  function setupATC() {
    const form = root.querySelector('[data-tcn-form]');
    if (!form) { console.warn('[Tucano PDP] No form found (data-tcn-form)'); return; }
    const atc = form.querySelector('[data-tcn-atc]');
    console.log('[Tucano PDP] ATC handler bound on form', form);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log('[Tucano PDP] ATC clicked');

      if (atc && atc.hasAttribute('disabled')) {
        console.warn('[Tucano PDP] Button disabled, abort');
        return;
      }
      if (atc) atc.classList.add('is-loading');

      // Diagnostic: log the variant id being submitted
      const variantInput = form.querySelector('[data-tcn-variant-input], input[name="id"]');
      console.log('[Tucano PDP] variant id =', variantInput?.value);

      try {
        const fd = new FormData(form);
        // Log every field being submitted (helps diagnose missing/invalid id)
        for (const [k, v] of fd.entries()) console.log('  →', k, '=', v);

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const body = await res.text();
        console.log('[Tucano PDP] /cart/add response', res.status, body.slice(0, 300));
        if (!res.ok) {
          throw new Error('Add to cart failed: ' + res.status);
        }

        // Aggiorna il contenuto del drawer e solo DOPO lo apre (niente flash di "carrello vuoto")
        await tcnSyncDrawer('tcn-pdp-form');
        await tcnOpenDrawer();

        // Visual feedback
        if (atc) atc.classList.remove('is-loading');
        const label = form.querySelector('[data-tcn-atc-label]');
        const original = label?.textContent;
        if (label) {
          label.textContent = (window.tcnI18n || {}).added || 'Aggiunto ✓';
          setTimeout(() => { if (original) label.textContent = original; }, 1800);
        }
      } catch (err) {
        console.error('[Tucano PDP] ATC fetch error', err);
        if (atc) {
          atc.classList.remove('is-loading');
          atc.dataset.error = '1';
        }
      }
    });
  }

  /* -------- Sticky bar visibility -------- */
  function setupStickyBar() {
    const bar = document.querySelector('[data-tcn-stickybar]');
    const card = root.querySelector('[data-tcn-card]');
    if (!bar || !card) return;

    const cta = bar.querySelector('[data-tcn-stickybar-cta]');
    if (cta) {
      cta.addEventListener('click', () => {
        const atc = root.querySelector('[data-tcn-atc]');
        if (atc) atc.click();
      });
    }

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        bar.classList.toggle('is-visible', !e.isIntersecting);
      });
    }, { rootMargin: '-50% 0px 0px 0px', threshold: 0 });
    io.observe(card);
  }

  /* -------- Sticky bar: variant chips (mirror of hero option radios) -------- */
  function setupStickyBarOptions() {
    const bar = document.querySelector('[data-tcn-stickybar]');
    if (!bar) return;

    const chips = bar.querySelectorAll('[data-tcn-stickybar-chip]');
    const cta = bar.querySelector('[data-tcn-stickybar-cta]');
    const ctaLabel = bar.querySelector('[data-tcn-stickybar-cta-label]');
    const ctaPrice = bar.querySelector('[data-tcn-stickybar-cta-price]');
    const priceEl = bar.querySelector('[data-tcn-stickybar-price]');

    function formatSticky(cents) {
      const fmt = window.Shopify?.currency?.active || 'EUR';
      const symbol = fmt === 'EUR' ? '€' : fmt + ' ';
      try {
        return symbol + new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
      } catch {
        return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
      }
    }

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const optIdx = chip.dataset.optionIndex;
        const value = chip.dataset.optionValue;
        const radio = root.querySelector(
          `[data-tcn-opt-radio][name="opt-${optIdx}"][value="${CSS.escape(value)}"]`
        );
        if (!radio) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    document.addEventListener('tcn:variant-change', (e) => {
      const variant = e.detail.variant;
      const selected = e.detail.selectedOptions || [];

      chips.forEach((chip) => {
        const optIdx = parseInt(chip.dataset.optionIndex, 10);
        chip.classList.toggle('is-active', selected[optIdx] === chip.dataset.optionValue);
      });

      if (priceEl) priceEl.textContent = formatSticky(variant.price);
      if (ctaPrice) ctaPrice.textContent = formatSticky(variant.price);

      if (cta) {
        cta.toggleAttribute('disabled', !variant.available);
        if (ctaLabel) ctaLabel.textContent = variant.available ? ((window.tcnI18n || {}).add || 'Aggiungi') : ((window.tcnI18n || {}).soldOut || 'Esaurito');
      }
    });

    /* -------- Quantity stepper: mirrors the hero's quantity input -------- */
    const qtyWrap = bar.querySelector('[data-tcn-stickybar-qty]');
    const qtyMinus = bar.querySelector('[data-tcn-stickybar-qty-minus]');
    const qtyPlus = bar.querySelector('[data-tcn-stickybar-qty-plus]');
    const qtyValueEl = bar.querySelector('[data-tcn-stickybar-qty-value]');
    const mainQtyInput = root.querySelector('[data-tcn-qty-input]');
    if (qtyWrap && mainQtyInput) {
      const syncFromMain = () => { if (qtyValueEl) qtyValueEl.textContent = mainQtyInput.value; };
      qtyMinus?.addEventListener('click', () => {
        mainQtyInput.value = Math.max(1, (parseInt(mainQtyInput.value, 10) || 1) - 1);
        mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        syncFromMain();
      });
      qtyPlus?.addEventListener('click', () => {
        mainQtyInput.value = (parseInt(mainQtyInput.value, 10) || 1) + 1;
        mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        syncFromMain();
      });
      mainQtyInput.addEventListener('input', syncFromMain);
      syncFromMain();
    }
  }

  /* -------- Quick-add complementary products (hero routine block) -------- */
  function setupRoutineQuickAdd() {
    const routine = root.querySelector('[data-tcn-routine]');
    if (!routine) return;

    function openDrawer() {
      const d = document.querySelector('cart-drawer-component');
      if (!d) return;
      const dialog = d.querySelector('dialog[ref="dialog"], dialog');
      if (dialog && dialog.open) return;
      if (typeof d.open === 'function') { try { d.open(); return; } catch (_) {} }
      if (typeof d.showDialog === 'function') { try { d.showDialog(); return; } catch (_) {} }
      if (dialog && typeof dialog.showModal === 'function') { try { dialog.showModal(); } catch (_) {} }
    }

    routine.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-tcn-routine-add]');
      if (!btn) return;
      e.preventDefault();
      const variantId = btn.dataset.tcnRoutineVariant;
      if (!variantId) return;

      btn.disabled = true;
      try {
        const fd = new FormData();
        fd.append('id', String(variantId));
        fd.append('quantity', '1');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        if (!res.ok) throw new Error('add failed ' + res.status);
        const result = await res.json();

        await tcnSyncDrawer('tcn-pdp-routine');
        await tcnOpenDrawer();

        // Visual success feedback
        btn.classList.add('is-added');
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1600);
      } catch (err) {
        console.error('[tcn-pdp routine] add error', err);
        btn.disabled = false;
      }
    });
  }

  /* -------- Custom dropdown for variant options (non-color) -------- */
  function setupDropdowns() {
    const dropdowns = root.querySelectorAll('[data-tcn-dropdown]');
    dropdowns.forEach((dd) => {
      const trigger = dd.querySelector('[data-tcn-dropdown-trigger]');
      const panel = dd.querySelector('[data-tcn-dropdown-panel]');
      const items = dd.querySelectorAll('[data-tcn-dropdown-item]');
      const current = dd.querySelector('[data-tcn-dropdown-current]');
      if (!trigger || !panel) return;

      // The fieldset is the parent — radios live in the same fieldset
      const fieldset = dd.closest('[data-tcn-opt]');
      if (!fieldset) return;

      const open = () => {
        panel.hidden = false;
        // Force reflow so animation runs
        void panel.offsetHeight;
        dd.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        dd.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        // Wait for transition before unmounting (keeps animation smooth)
        setTimeout(() => { if (!dd.classList.contains('is-open')) panel.hidden = true; }, 220);
      };
      const toggle = () => dd.classList.contains('is-open') ? close() : open();

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        toggle();
      });

      items.forEach((item) => {
        item.addEventListener('click', () => {
          const value = item.dataset.tcnDropdownValue;
          if (value === undefined) return;
          // Find the matching radio inside the same fieldset and check it
          const radio = Array.from(fieldset.querySelectorAll('[data-tcn-opt-radio]'))
            .find((r) => r.value === value);
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          // Update trigger label + selected state
          if (current) current.textContent = (value || '').toLowerCase();
          items.forEach((i) => {
            const sel = i === item;
            i.classList.toggle('is-selected', sel);
            i.setAttribute('aria-selected', sel ? 'true' : 'false');
          });
          close();
          trigger.focus();
        });

        // Keyboard support: Enter / Space activates an item
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            item.click();
          }
        });
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!dd.contains(e.target) && dd.classList.contains('is-open')) close();
      });

      // ESC chiude
      dd.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && dd.classList.contains('is-open')) {
          e.preventDefault();
          close();
          trigger.focus();
        }
      });

      // Arrow nav within panel
      panel.addEventListener('keydown', (e) => {
        const list = Array.from(items);
        const focused = document.activeElement;
        const idx = list.indexOf(focused);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = list[Math.min(idx + 1, list.length - 1)] || list[0];
          next?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = list[Math.max(idx - 1, 0)] || list[list.length - 1];
          prev?.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          list[0]?.focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          list[list.length - 1]?.focus();
        }
      });

      // Auto-focus first item when opening
      trigger.addEventListener('click', () => {
        if (dd.classList.contains('is-open')) {
          const first = dd.querySelector('.tcn-dropdown__item.is-selected') || items[0];
          setTimeout(() => first?.setAttribute('tabindex', '0'), 0);
        }
      });
    });
  }

  /* -------- Measure trust strip height for hero calc -------- */
  function setTrustStripVar() {
    const trust = document.querySelector('.tcn-trust-strip');
    if (!trust) return;
    const update = () => {
      const h = trust.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--tcn-trust-strip-h', h + 'px');
    };
    update();
    // Track live (fonts load, content shifts)
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(trust);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update);
    }
  }

  /* -------- P1.6 — 3D / AR badge + dialog --------
     Il modello NON è dentro la gallery: vive in un <dialog> dedicato.
     Il badge è visibile solo se la posizione del modello cade nel
     range di media della variante corrente. */
  let currentVariantRange = null; // {start, end} or null = mostra sempre

  function refresh3DBadge() {
    const badge = root.querySelector('[data-tcn-3d-badge]');
    if (!badge) return;
    const modelPos = parseInt(badge.dataset.modelPosition || '0', 10);
    if (!modelPos) {
      badge.hidden = true;
      return;
    }
    if (!currentVariantRange) {
      // Nessuno scope variante applicato → mostra sempre
      badge.hidden = false;
      return;
    }
    const inScope = modelPos >= currentVariantRange.start && modelPos <= currentVariantRange.end;
    badge.hidden = !inScope;
  }

  function setup3DBadge() {
    const badge = root.querySelector('[data-tcn-3d-badge]');
    const dialog = document.querySelector('[data-tcn-3d-dialog]');
    if (!badge || !dialog) return;

    refresh3DBadge();

    const closeBtn = dialog.querySelector('[data-tcn-3d-dialog-close]');
    const mv = dialog.querySelector('model-viewer');

    const open = async () => {
      if (typeof dialog.showModal === 'function') {
        try { dialog.showModal(); } catch (_) { dialog.setAttribute('open', ''); }
      } else {
        dialog.setAttribute('open', '');
      }
      // Solo mobile: lancia AR appena il modello è caricato
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile || !mv) return;
      try {
        if (window.customElements && window.customElements.whenDefined) {
          await window.customElements.whenDefined('model-viewer');
        }
      } catch (_) { /* noop */ }
      const tryAR = () => {
        if (typeof mv.activateAR === 'function' && mv.canActivateAR) {
          try { mv.activateAR(); } catch (_) {}
        }
      };
      if (mv.loaded) tryAR();
      else mv.addEventListener('load', tryAR, { once: true });
    };

    const close = () => {
      if (typeof dialog.close === 'function') {
        try { dialog.close(); } catch (_) { dialog.removeAttribute('open'); }
      } else {
        dialog.removeAttribute('open');
      }
    };

    badge.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    // Click sul backdrop (fuori dal contenuto) chiude
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) close();
    });
    // ESC chiude (gestito nativamente da <dialog>, ma assicuriamoci)
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      close();
    });
  }

  /* -------- ODDS · Side drawer (Prada-style) -------- */
  function setupOddsDrawer() {
    const triggers = document.querySelectorAll('[data-tcn-drawer-open]');
    const drawers = document.querySelectorAll('[data-tcn-drawer]');
    if (!triggers.length || !drawers.length) return;

    let lastFocus = null;

    const open = (name) => {
      const target = document.querySelector(`[data-tcn-drawer="${name}"]`);
      if (!target) return;
      lastFocus = document.activeElement;
      target.classList.add('is-open');
      target.setAttribute('aria-hidden', 'false');
      document.body.classList.add('tcn-odds-drawer-open');
      // Focus to close button after animation
      setTimeout(() => {
        const closeBtn = target.querySelector('[data-tcn-drawer-close]');
        if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
      }, 100);
    };

    const closeAll = () => {
      drawers.forEach((d) => {
        d.classList.remove('is-open');
        d.setAttribute('aria-hidden', 'true');
      });
      document.body.classList.remove('tcn-odds-drawer-open');
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try { lastFocus.focus(); } catch (_) {}
      }
    };

    triggers.forEach((t) => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        open(t.dataset.tcnDrawerOpen);
      });
    });

    // Close on backdrop / close button / Escape
    drawers.forEach((d) => {
      d.querySelectorAll('[data-tcn-drawer-close]').forEach((el) => {
        el.addEventListener('click', closeAll);
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('tcn-odds-drawer-open')) {
        closeAll();
      }
    });
  }

  /* -------- Init -------- */
  async function init() {
    await loadProduct();
    setupGallery();
    setupVariants();
    setupDropdowns();
    setupQty();
    setupATC();
    setupRoutineQuickAdd();
    setupStickyBar();
    setupStickyBarOptions();
    setup3DBadge();
    setupOddsDrawer();
    setTrustStripVar();
    window.addEventListener('resize', setTrustStripVar, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
